/**
 * Notification API - in-app alerts for the authenticated user.
 */
import { apiGet, apiGetRaw, apiPut, apiPutRaw } from "./client";
import type { ApiResponse, PaginatedResponse } from "@/types";

export interface Notification {
  id: string;
  organization_id: string;
  user_id: string;
  type: string;
  title: string;
  body: string;
  icon_url?: string;
  patient_id?: string;
  resource_type?: string;
  resource_id?: string;
  action_url?: string;
  channel: string;
  status: string;
  scheduled_for?: string;
  sent_at?: string;
  read_at?: string;
  is_urgent: boolean;
  created_at: string;
  updated_at: string;
}

/** List notifications for the current user. */
export async function listNotifications(params?: {
  page?: number;
  per_page?: number;
  type?: string;
  status?: string;
  unread?: boolean;
}): Promise<PaginatedResponse<Notification>> {
  const resp = await apiGetRaw<ApiResponse<Notification[]>>("/notifications", { params });
  const meta = resp.meta as PaginatedResponse<Notification>["meta"] | undefined;
  return {
    data: resp.data ?? [],
    meta: meta ?? { page: 1, per_page: 20, total_items: 0, total_pages: 0 },
  };
}

/** Unread notification count. */
export function getUnreadNotificationCount(): Promise<number> {
  return apiGet<{ unread: number }>("/notifications/unread-count").then((r) => r.unread);
}

/** Mark one notification as read. */
export function markNotificationRead(id: string): Promise<void> {
  return apiPutRaw<void>(`/notifications/${id}/read`);
}

/** Mark all notifications as read. */
export function markAllNotificationsRead(): Promise<void> {
  return apiPutRaw<void>("/notifications/read-all");
}

// ── Personal notification preferences ────────────────────────────────────────

export type NotificationPrefChannel = "in_app" | "email" | "sms";

export interface NotificationPreference {
  notification_type: string;
  channel: NotificationPrefChannel;
  enabled: boolean;
}

/** The signed-in user's saved choices. A type/channel with no row is on. */
export function listNotificationPreferences(): Promise<NotificationPreference[]> {
  return apiGet<NotificationPreference[]>("/notifications/preferences");
}

export function updateNotificationPreference(pref: NotificationPreference): Promise<NotificationPreference> {
  return apiPut<NotificationPreference>("/notifications/preferences", pref);
}
